import { useState } from 'react';
import { GovMessage } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { MarkdownRender } from '@gov-nx/component/web';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useDataBoxEvents } from '@gov-nx/core/events';
import { useLocale } from '@gov-nx/core/service';
import type { Optional } from '@gov-nx/core/types';
import { PageCode, useDataboxAdditionalSettings } from '@gov-nx/module/page';
import {
	Button,
	Dialog,
	FormSwitch,
	Icon,
	LayoutGap,
	Message,
} from '@gov-nx/ui/web';

export const DataboxConnectedSettingsModal = () => {
	const { t: tsn } = useLocale(PageCode['datove-schranky']);
	const [databoxId, setDataboxId] = useState<Optional<string>>();
	const { extraConfig } = useExtraConfigContext();

	useDataBoxEvents({
		onShowDataBoxSettingModal: (_, payload) => {
			if (
				!extraConfig.pageRoutes.databoxConfig.displayPopupAfterDataboxConnected
			) {
				return;
			}
			setDataboxId(payload.databoxId);
		},
	});

	const {
		formId,
		formDefinitions,
		onSubmit,
		isLoading,
		isDisabled,
		isInitialLoading,
		emailVerified,
		isArchivingAvailable,
		showLargeMessageArchive,
	} = useDataboxAdditionalSettings({
		dataBoxId: databoxId,
		onSuccess: () => setDataboxId(undefined),
	});

	if (isInitialLoading) {
		return null;
	}

	return (
		<Dialog
			isOpen={!!databoxId}
			onClose={() => setDataboxId(undefined)}
			title={<h3>{tsn('datove-schranky-pripojeni-modal.nadpis')}</h3>}
			footer={
				<Button
					form={formId}
					isExpandedMobile
					aria-label={tsn(
						'datove-schranky-pripojeni-modal.tlacitko.pokracovat'
					)}
					isDisabled={isDisabled || isLoading}
					isLoading={isLoading}
					nativeType="submit"
					color="primary"
					size="l"
					type="solid">
					{tsn('datove-schranky-pripojeni-modal.tlacitko.pokracovat')}
				</Button>
			}>
			<GovFormWeb
				id={formId}
				onSubmit={onSubmit}
				formDefinition={formDefinitions}>
				<LayoutGap gap="l">
					{extraConfig.pageRoutes.databoxConfig
						.displayArchiveOptionsInInitialModal && (
						<>
							<MarkdownRender>
								{tsn('datove-schranky-pripojeni-modal.popis')}
							</MarkdownRender>
							<LayoutGap gap="m">
								{isArchivingAvailable ? (
									<>
										<div>
											<FormSwitch
												field={{
													name: 'shouldArchiveAutomatically',
													isDisabled,
													label: {
														children: tsn('automaticka-archivace.label'),
														identifier: `label-${databoxId}-shouldArchiveAutomatically`,
													},
													'aria-describedby':
														'automaticka-archivace-plny-popis',
												}}
											/>
											<LayoutGap gap="s">
												<p
													id="automaticka-archivace-plny-popis"
													className="text-body-xs md:text-body-s text-(--text-secondary)">
													{tsn('automaticka-archivace.plny-popis')}
												</p>
												<MarkdownRender
													applyClasses={{
														paragraph: 'text-xs md:text-s text-secondary-700',
													}}>
													{tsn('automaticka-archivace.varovani.fikce')}
												</MarkdownRender>
											</LayoutGap>
										</div>

										{showLargeMessageArchive ? (
											<div>
												<FormSwitch
													field={{
														name: 'shouldArchiveLargeMessagesAutomatically',
														isDisabled,
														label: {
															children: tsn(
																'automaticka-archivace-velke-zpravy.label'
															),
															identifier: `label-${databoxId}-shouldArchiveLargeMessagesAutomatically`,
														},
														'aria-describedby':
															'automaticka-archivace-velke-zpravy-plny-popis',
													}}
												/>
												<p
													id="automaticka-archivace-velke-zpravy-plny-popis"
													className="text-body-xs md:text-body-s text-(--text-secondary)">
													{tsn('automaticka-archivace-velke-zpravy.plny-popis')}
												</p>
											</div>
										) : null}
									</>
								) : (
									<GovMessage
										color="warning"
										type="subtle"
										icon={
											<Icon
												type="components"
												name="info-circle-fill"
											/>
										}
										className="ml-4 mb-8">
										<p>{tsn('automaticka-archivace.nedostupna-PO')}</p>
									</GovMessage>
								)}
							</LayoutGap>
						</>
					)}

					<LayoutGap gap="m">
						<div>
							<FormSwitch
								field={{
									name: 'emailEnabled',
									isDisabled: isDisabled || !emailVerified,
									label: {
										children: tsn('datove-schranky-pripojeni-modal.email'),
									},
									...(emailVerified && {
										'aria-describedby': `popisek-nastaveni-upozorneni-notifikace_${databoxId}`,
									}),
								}}
							/>
							{emailVerified ? (
								<MarkdownRender
									id={`popisek-nastaveni-upozorneni-notifikace_${databoxId}`}
									disableGithubFlavoredMarkdown
									applyClasses={{
										paragraph:
											'text-body-xs md:text-body-s text-(--text-secondary)',
									}}>
									{tsn('datove-schranky-pripojeni-modal.email-popis', {
										email: emailVerified,
									})}
								</MarkdownRender>
							) : null}
						</div>
						{!emailVerified && (
							<Message
								color="neutral"
								type="bold"
								icon={
									<Icon
										type="components"
										name="exclamation-triangle-fill"
									/>
								}>
								{tsn('upozorneni.zprava.zadny-email')}
							</Message>
						)}
					</LayoutGap>
				</LayoutGap>
			</GovFormWeb>
		</Dialog>
	);
};
